import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Messages } from 'src/app/data';
import { MessageService } from 'src/app/modules/message/services/message/message.service';
import { ReferrerService } from 'src/app/shared/services/referrer/referrer.service';
import {
  LoggingService,
  LogData,
} from 'src/app/shared/services/logging/logging.service';

@Component({
  selector: 'htc-messages-debug-menu',
  imports: [CommonModule, FormsModule],
  templateUrl: './messages-debug-menu.component.html',
  styleUrl: './messages-debug-menu.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessagesDebugMenuComponent {
  readonly messages = Messages;
  readonly Object = Object;

  messageCode = 0;

  referrer = '';

  // Logging test properties
  logType = 'debug_test';
  logData = '{"action": "button_click", "elementId": "test-button"}';
  forceActivityId = '';
  isLoggingTest = false;
  lastLogResult: boolean | null = null;
  logError = '';

  constructor(
    private messageService: MessageService,
    private referrerService: ReferrerService,
    private loggingService: LoggingService
  ) {
    this.referrer = this.referrerService.getReferrer();
  }

  showMessage(): void {
    this.messageService.showMessage(this.messageCode);
  }

  setReferrer(): void {
    this.referrerService.setReferrer(this.referrer);
  }

  async testLoggingService(): Promise<void> {
    if (this.isLoggingTest) {
      return;
    }

    this.isLoggingTest = true;
    this.lastLogResult = null;
    this.logError = '';

    try {
      // Parse the JSON data
      let parsedData: LogData;
      try {
        parsedData = JSON.parse(this.logData);
      } catch (parseError) {
        this.logError = 'Invalid JSON format in log data';
        return;
      }

      console.log('Starting logging test with data:', {
        type: this.logType,
        data: parsedData,
        forceKey: this.forceActivityId || 'none',
      });

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<boolean>((_, reject) => {
        setTimeout(
          () =>
            reject(new Error('Logging operation timed out after 10 seconds')),
          10000
        );
      });

      // Call the logging service with timeout
      const result = await Promise.race([
        this.loggingService.logInfo(
          this.logType,
          parsedData,
          this.forceActivityId || undefined
        ),
        timeoutPromise,
      ]);

      this.lastLogResult = result;

      if (result) {
        console.log('✅ Logging test successful');
        this.logError = '';
      } else {
        console.warn(
          '⚠️ Logging returned false - check feature flag and configuration'
        );
        this.logError =
          'Logging returned false. Check that enableLoggingService feature flag is enabled and AWS is configured.';
      }
    } catch (error) {
      console.error('❌ Logging test error:', error);
      this.logError = `Logging error: ${
        error instanceof Error ? error.message : String(error)
      }`;
      this.lastLogResult = false;
    } finally {
      this.isLoggingTest = false;
      console.log('Logging test completed, isLoggingTest reset to false');
    }
  }

  resetLogTest(): void {
    this.logType = 'debug_test';
    this.logData = '{"action": "button_click", "elementId": "test-button"}';
    this.forceActivityId = '';
    this.lastLogResult = null;
    this.logError = '';
  }
}
